import { useCallback, useEffect, useState } from 'react'
import { Spinner } from 'components'
import { useTranslation } from 'react-i18next'
import UnvModal from 'components/UnvDialog/UnvModal'
import {
  listRoles,
  createRole,
  updateRole,
  deleteRole,
  seedDefaultRoles,
} from 'service/AdminPanelService'
import styles from '../AdminFullPage.module.scss'

function RolesTab({ notify }) {
  const { t } = useTranslation('common')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ code: '', title: '', description: '' })

  const load = useCallback(() => {
    setLoading(true)
    listRoles()
      .then((res) => {
        const data = res?.data || res
        setItems(Array.isArray(data) ? data : data?.results || [])
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm({ code: '', title: '', description: '' })
    setModalOpen(true)
  }

  const openEdit = (role) => {
    setEditing(role)
    setForm({
      code: role.code || '',
      title: role.title || '',
      description: role.description || '',
    })
    setModalOpen(true)
  }

  const onSave = () => {
    const action = editing
      ? updateRole(editing.id, form)
      : createRole(form)
    action
      .then(() => {
        notify(editing ? t('admin.fullPage.roles_updated') : t('admin.fullPage.roles_created'), 'success')
        setModalOpen(false)
        load()
      })
      .catch(() => notify(t('admin.fullPage.roles_save_error'), 'error'))
  }

  const onDelete = (role) => {
    if (!role?.id) return
    deleteRole(role.id)
      .then(() => {
        notify(t('admin.fullPage.roles_deleted'), 'success')
        load()
      })
      .catch(() => notify(t('admin.fullPage.roles_delete_error'), 'error'))
  }

  const onSeed = () => {
    seedDefaultRoles()
      .then(() => {
        notify(t('admin.fullPage.roles_seed_success'), 'success')
        load()
      })
      .catch(() => notify(t('admin.fullPage.roles_seed_error'), 'error'))
  }

  return (
    <div>
      <h2 className={styles.section_title}>{t('admin.fullPage.roles_title')}</h2>

      <div className={styles.toolbar}>
        <button type="button" className="unv-btn" onClick={openCreate}>
          {t('admin.fullPage.roles_create')}
        </button>
        <button
          type="button"
          className="unv-btn unv-btn--ghost"
          onClick={onSeed}
        >
          {t('admin.fullPage.roles_seed')}
        </button>
      </div>

      {loading ? (
        <div className={styles.spinner_box}>
          <Spinner.Center />
        </div>
      ) : items.length === 0 ? (
        <div className="unv-empty">{t('admin.fullPage.roles_empty')}</div>
      ) : (
        <table className="unv-table">
          <thead>
            <tr>
              <th>{t('admin.fullPage.roles_th_code')}</th>
              <th>{t('admin.fullPage.roles_th_title')}</th>
              <th>{t('admin.fullPage.roles_th_description')}</th>
              <th style={{ width: 200 }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id || r.code}>
                <td>{r.code}</td>
                <td>{r.title || '—'}</td>
                <td>{r.description || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="unv-btn unv-btn--ghost unv-btn--sm"
                    onClick={() => openEdit(r)}
                    style={{ marginRight: 6 }}
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    type="button"
                    className="unv-btn unv-btn--danger unv-btn--sm"
                    onClick={() => onDelete(r)}
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <UnvModal
        open={modalOpen}
        title={editing ? t('admin.fullPage.roles_modal_edit') : t('admin.fullPage.roles_modal_create')}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="unv-btn unv-btn--ghost"
              onClick={() => setModalOpen(false)}
            >
              {t('common.cancel')}
            </button>
            <button type="button" className="unv-btn" onClick={onSave}>
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <label className="unv-label">{t('admin.fullPage.roles_th_code')}</label>
            <input
              className="unv-input"
              value={form.code}
              onChange={(e) =>
                setForm((s) => ({ ...s, code: e.target.value }))
              }
              disabled={!!editing}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="unv-label">{t('admin.fullPage.roles_th_title')}</label>
            <input
              className="unv-input"
              value={form.title}
              onChange={(e) =>
                setForm((s) => ({ ...s, title: e.target.value }))
              }
            />
          </div>
        </div>
        <div>
          <label className="unv-label">{t('admin.fullPage.roles_th_description')}</label>
          <textarea
            className="unv-textarea"
            value={form.description}
            onChange={(e) =>
              setForm((s) => ({ ...s, description: e.target.value }))
            }
          />
        </div>
      </UnvModal>
    </div>
  )
}

export default RolesTab
